/**
 * CSS Styles and Typography Configuration for Readium 2 EPUB Viewer.
 *
 * This file centralizes all injectable CSS styles and font size settings
 * used by the Readium 2 navigator to render EPUB content.
 */

// Import accessible fonts CSS as raw string (shared with fonts.scss)
import accessibleFontsCssRaw from '../../../../../sass/accessible-reader-fonts.css?raw';

// ─────────────────────────────────────────────────────────────────────────────
// Font Size Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Font size limits for EPUB zoom (in REM units, where 1 = 100%).
 * These values control the min/max zoom levels available to users.
 */
export const FONT_SIZE_MIN = 0.75; // 75%
export const FONT_SIZE_MAX = 4; // 400%
export const FONT_SIZE_STEP = 0.25; // 25% increment per click
export const FONT_SIZE_DEFAULT = 1; // 100%

// Publisher CSS assumes a 16px browser base. The reader opens it at 18px
// (Apple Books opens the same book near 19px) as a zoom of the whole body,
// so pt and px sizes in publisher CSS scale with it. The setting's 100%
// stays 100% for the reader.
export const TEXT_SIZE_BASE_ZOOM = 1.125; // 18px for a 16px publisher base

// The navigator's ceiling on the zoom the setting is scaled into (the
// setting's own ceiling is FONT_SIZE_MAX). A value past it is dropped, not
// clamped, so the top settings pin to it instead.
export const NAVIGATOR_FONT_SIZE_MAX = 4;

/**
 * The zoom factor the navigator applies for a text-size setting.
 * @param {number|null|undefined} fontSize - the setting (1 = 100%)
 * @returns {number}
 */
export const getEffectiveZoom = fontSize =>
    Math.min(NAVIGATOR_FONT_SIZE_MAX, Math.round((fontSize ?? FONT_SIZE_DEFAULT) * TEXT_SIZE_BASE_ZOOM * 1000) / 1000);

/**
 * API format conversion constants.
 * The API uses integers where 120 = 100% (base value).
 * This allows for finer granularity without floating point issues.
 *
 * Conversion formulas:
 *   - REM to API: remValue * API_FONT_SIZE_BASE
 *   - API to REM: apiValue / API_FONT_SIZE_BASE
 *
 * Examples:
 *   - 75% (0.75 rem) = 90 API
 *   - 100% (1.0 rem) = 120 API
 *   - 400% (4.0 rem) = 480 API
 */
export const API_FONT_SIZE_BASE = 120; // API value for 100%
export const API_FONT_SIZE_MIN = 90; // API minimum (75%)
export const API_FONT_SIZE_MAX = 480; // API maximum (400%)

// ─────────────────────────────────────────────────────────────────────────────
// Line Height Configuration
// ─────────────────────────────────────────────────────────────────────────────

export const LINE_HEIGHT_COMPACT = 1.2;
export const LINE_HEIGHT_NORMAL = 1.5;
export const LINE_HEIGHT_WIDE = 2.0;
export const LINE_HEIGHT_DEFAULT = LINE_HEIGHT_NORMAL;

// ─────────────────────────────────────────────────────────────────────────────
// Paragraph Spacing Configuration
// ─────────────────────────────────────────────────────────────────────────────

// Vertical space added after paragraphs (in REM), mapped by Readium to
// --USER__paraSpacing (margin-top/bottom on <p>). Because the --USER__ prefix
// wins the cascade (USER > PUBLISHER > RS), this replaces the publisher's
// paragraph margins rather than stacking on top of them, so it cannot
// double-space EPUBs that already define their own <p> margins.
//
// There is no separate UI control. Line-height separates the lines within a
// paragraph, while paragraph spacing separates adjacent paragraphs. Many EPUBs
// use the compact novel style (text-indent, zero margin), where raising
// line-height alone still leaves paragraphs packed together. Deriving the
// spacing from the existing line-spacing setting keeps one control while still
// separating adjacent paragraphs.

// Paragraph spacing range (rem). The minimum applies at the compact line-height
// so paragraphs are never packed together. The maximum is reached at the wide
// line-height. The range tracks the low end of Thorium Reader's paragraph
// spacing scale (first non-zero step 0.5rem, up to 3rem), so the effect is
// noticeable without overwhelming prose.
export const PARAGRAPH_SPACING_MIN = 0.5; // rem, at LINE_HEIGHT_COMPACT
export const PARAGRAPH_SPACING_MAX = 1.0; // rem, at LINE_HEIGHT_WIDE

// Derives paragraph spacing (rem) from the chosen line-height by linearly
// interpolating across the line-height range, so spacing rises smoothly with
// line spacing instead of jumping. The result is clamped to
// [PARAGRAPH_SPACING_MIN, PARAGRAPH_SPACING_MAX] and rounded to 0.25rem steps:
// compact (1.2) -> 0.5, normal (1.5) -> 0.75, wide (2.0) -> 1.0.
//
// A null line-height means the reader has not touched the typography, and
// then the paragraph spacing stays the publisher's too (null). Apple Books,
// Kindle and Thorium all open a book with the publisher's paragraph style;
// forcing spacing onto an indented novel turns it into web-page paragraphs
// the designer never chose, and doubles the gaps in books that already space
// their paragraphs.
export const getParagraphSpacing = lineHeight => {
    if (lineHeight == null) {
        return null;
    }
    const ratio = (lineHeight - LINE_HEIGHT_COMPACT) / (LINE_HEIGHT_WIDE - LINE_HEIGHT_COMPACT);
    const clampedRatio = Math.min(1, Math.max(0, ratio));
    const spacing = PARAGRAPH_SPACING_MIN + clampedRatio * (PARAGRAPH_SPACING_MAX - PARAGRAPH_SPACING_MIN);
    return Math.round(spacing * 4) / 4;
};

/**
 * The paragraph preferences that go with a line-height setting. A paragraph
 * is set apart either by an indent or by a gap, never both: the derived
 * spacing turns the publisher's indented paragraphs into blocks, so the
 * indent comes off with it. With the publisher's line-height (null) all
 * three stay the publisher's.
 * @param {number|null|undefined} lineHeight
 * @returns {{ lineHeight: number|null, paragraphSpacing: number|null, paragraphIndent: number|null }}
 */
export const getParagraphPreferences = lineHeight => {
    const paragraphSpacing = getParagraphSpacing(lineHeight);
    return {
        lineHeight: lineHeight ?? null,
        paragraphSpacing,
        paragraphIndent: paragraphSpacing === null ? null : 0,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Margins Configuration (pageGutter values in pixels)
// ─────────────────────────────────────────────────────────────────────────────

export const MARGINS_MINIMUM = 5;
export const MARGINS_NORMAL = 20;
export const MARGINS_MAXIMUM = 60;
// Shrinking the gutter below its initial value is safe: the gutter-compensated
// line lengths keep the container width constant (see getLineLengths).
export const MARGINS_DEFAULT = MARGINS_NORMAL;

// ─────────────────────────────────────────────────────────────────────────────
// Double-page Spread Gutter
// ─────────────────────────────────────────────────────────────────────────────

// ReadiumCSS paginates with `column-gap: 0` and builds the gutter from the
// body's horizontal padding, which repeats in every column, so the centre gap
// of a spread is twice the gutter and nothing else. A rendered spread adds this
// share of the reader width to each column side. The compensated line lengths
// pin the spread width, so past this reader width the allowance would only
// keep narrowing the text.
export const SPREAD_GUTTER_RATIO = 0.025;
export const SPREAD_GUTTER_MAX_WIDTH = 2560;

export const getSpreadGutter = (margins, readerWidth) =>
    margins + Math.round(Math.min(readerWidth || 0, SPREAD_GUTTER_MAX_WIDTH) * SPREAD_GUTTER_RATIO);

// ─────────────────────────────────────────────────────────────────────────────
// Line Length Configuration (values in characters)
// ─────────────────────────────────────────────────────────────────────────────

// Reading-system line-length bounds, in characters. Readium2 converts them to
// pixels and uses them to compute viewport and column widths; the maximal
// bound caps the body width inside content iframes.
//
// The minimal bound is the floor under a column of a double-page spread: the
// navigator drops to one page when two columns cannot each hold this many
// characters (see paginate() in its ReadiumCSS helper). Below about 40 the
// justified text of a narrow column fills with rivers, which is where Apple
// Books gives up the second page too.
export const LINE_LENGTH_MINIMAL = 40;
export const LINE_LENGTH_OPTIMAL = 55;
export const LINE_LENGTH_MAXIMAL = 75;
// A single column has the whole window to itself, so it may run longer than a
// page of a spread. 85 is the measure Apple Books gives each page.
export const LINE_LENGTH_MAXIMAL_SINGLE = 85;

// Readium2's LineLengths helper converts the character bounds to pixels by
// measuring a repeated "0" digit, and digits are 0.5em wide (8px at the 16px
// base font size) in virtually every text font.
const LINE_LENGTH_CHAR_WIDTH_PX = 8;

// Line-length bounds compensated for the horizontal gutter (DEV-4883).
//
// Readium2 adds the horizontal gutter (pageGutter in paged mode,
// scrollPaddingLeft/Right in scroll mode) on top of the character bounds when
// converting them to pixels. On viewports wider than the maximal line length
// the body is capped at that bound and centered, so raising the margins grows
// the cap by exactly the padding the gutter consumes: the text block keeps the
// same width and position, and the margins setting has no visible effect in
// single-column layouts (horizontal/single page and vertical scroll).
//
// Subtracting the gutter from the bounds cancels that out: the rendered cap
// stays constant in pixels and the gutter visibly insets the text, exactly as
// it does on viewport-bound (mobile) widths where the cap never kicks in.
// It also keeps the navigator's computed container width stable across margin
// changes, avoiding a full column-relayout jump on every margins click.
//
// The minimal bound is not compensated: it is a floor under the text itself,
// so the gutter the navigator adds on top is exactly right there, and the
// other two bounds stay above it by construction. The cap is the
// single-column one unless the bounds are for a double-page spread.
export const getLineLengths = (margins, { isSpread = false } = {}) => {
    const gutterChars = Math.round((2 * (margins ?? MARGINS_DEFAULT)) / LINE_LENGTH_CHAR_WIDTH_PX);
    const maximal = isSpread ? LINE_LENGTH_MAXIMAL : LINE_LENGTH_MAXIMAL_SINGLE;
    return {
        minimalLineLength: LINE_LENGTH_MINIMAL,
        optimalLineLength: Math.max(LINE_LENGTH_OPTIMAL - gutterChars, LINE_LENGTH_MINIMAL + 1),
        maximalLineLength: Math.max(maximal - gutterChars, LINE_LENGTH_MINIMAL + 2),
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Font-size Zoom Compensation for Pixel Paddings
// ─────────────────────────────────────────────────────────────────────────────

// Readium2 applies the font-size setting as CSS `zoom` on the content body
// (when the browser supports `zoom` and outside the iOS mobile patch). The
// pixel paddings submitted through preferences (pageGutter, scrollPadding*)
// live inside that zoomed coordinate space, so the browser paints them
// multiplied by the font-size factor: at 175% a 60px gutter paints as 105px
// per side — on a 360px phone the two gutters swallow 58% of the screen and
// squeeze the text into a sliver. Below 100% the inverse happens: the
// header/footer scroll clearances paint smaller than the bars they must
// clear. The navigator already divides its computed line length by the zoom
// factor for exactly this reason, but not the pixel preferences.
//
// Dividing the pixel value by the zoom factor before submitting keeps the
// painted size equal to the configured value at every font size. When
// font-size is applied without zoom (no CSS zoom support, or the iOS mobile
// `-webkit-text-size-adjust` patch), paddings are not scaled by the browser
// and must be submitted as-is — callers pass `zoomBasedFontSizing` for that
// environment check so this helper stays pure.
export const getZoomCompensatedPadding = (paddingPx, zoom, zoomBasedFontSizing) => {
    if (!zoomBasedFontSizing || !(zoom > 0) || zoom === 1) {
        return paddingPx;
    }
    return Math.round(paddingPx / zoom);
};

// ─────────────────────────────────────────────────────────────────────────────
// Injectable CSS Styles
// ─────────────────────────────────────────────────────────────────────────────

// Fixed header height, mirrored from --header-toolbar-height in _theme_ui.scss.
export const READER_HEADER_HEIGHT = 55;

/**
 * Floating previous/next-section button geometry (scroll mode). The Vue component
 * lays itself out from these and the scroll clearances below reserve room from the
 * same numbers, so the button and the text it must clear stay in agreement.
 */
export const SECTION_BUTTON_HEIGHT = 40;
// Distance between the button and the bar it hangs from (or the screen edge once the bar hides).
export const SECTION_BUTTON_BAR_GAP = 12;
// Slack between the button and the nearest text line.
const SECTION_BUTTON_TEXT_GAP = 4;
// Horizontal room the rail keeps beside the default text column on each side,
// so on wide screens the buttons hang next to the text instead of at the far edges.
const SECTION_BUTTON_COLUMN_SIDE_ROOM = 56;
// The rail hugs the default-size single text column (LINE_LENGTH_MAXIMAL_SINGLE
// chars at the base zoom) plus the side room. Narrower viewports clamp it to
// the screen.
export const SECTION_BUTTON_RAIL_MAX_WIDTH =
    Math.round(LINE_LENGTH_MAXIMAL_SINGLE * LINE_LENGTH_CHAR_WIDTH_PX * TEXT_SIZE_BASE_ZOOM) +
    2 * SECTION_BUTTON_COLUMN_SIDE_ROOM;
// Clearance (px) added to both scroll-mode paddings so the first and last lines
// also clear the floating section buttons, which hang off the bars at chapter ends.
export const SCROLL_SECTION_BUTTON_CLEARANCE = SECTION_BUTTON_BAR_GAP + SECTION_BUTTON_HEIGHT + SECTION_BUTTON_TEXT_GAP;

/**
 * Generates accessible fonts CSS with absolute URLs for EPUB iframes.
 * Uses the shared CSS from accessible-reader-fonts.css, replacing relative
 * URLs with absolute ones since iframes load content from blob: origins.
 *
 * @param {string} baseUrl - The origin URL (e.g., 'https://volpe.localhost')
 * @returns {string} CSS with @font-face declarations and inheritance override
 */
export const getAccessibleFontsCss = baseUrl => {
    // Replace relative font URLs with absolute URLs for iframe context
    const fontsWithAbsoluteUrls = accessibleFontsCssRaw.replace(
        /url\(\.\.\/fonts\//g,
        `url(${baseUrl}/volpe-assets/fonts/`
    );

    // Add override rule for ReadiumCSS 'revert' behavior
    return `${fontsWithAbsoluteUrls}
/* Override ReadiumCSS 'revert' rule - force inheritance for custom fonts */
:root[style*="--USER__fontFamily"] body *:not(code):not(var):not(kbd):not(samp) {
    font-family: inherit !important;
}`;
};

/**
 * Base rule injected into every chapter frame so the browser never resizes
 * the text on its own. iPhone WebKit applies text autosizing to any block
 * whose `-webkit-text-size-adjust` is `auto`, and rotating to landscape is
 * the classic trigger: the text grows, and the boost sticks when the phone
 * turns back. Chrome on Android does the same through `text-size-adjust`.
 *
 * ReadiumCSS only sets the property when the reader has chosen a font size
 * (its iOS patch applies `--USER__fontSize` as `-webkit-text-size-adjust` on
 * body, with `!important`), so the default state was exposed. This rule pins
 * the default; it carries no `!important`, so the font-size control keeps
 * winning whenever it is in use.
 */
export const FRAME_TEXT_SIZE_CSS = `
html, body {
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
}`;

/**
 * Marks a cover frame's root so the paged-flow reset below leaves it alone.
 * Set by the same pass that injects COVER_FRAME_CSS, which pins the cover's
 * body to the frame on purpose, the one case where a pinned height is right.
 */
export const COVER_FRAME_MARKER = 'data-volpe-cover';

/**
 * Marks the elements between a cover frame's body and its images. Of the body's
 * contents, only these and the images are sized by COVER_FRAME_CSS, so a hidden
 * heading stays hidden and a pagebreak marker takes no room.
 */
export const COVER_FRAME_WRAPPER_MARKER = 'data-volpe-cover-wrapper';

/**
 * The body of a reflowable chapter frame while it is paginated, cover aside.
 * Mirrors how ReadiumCSS itself scopes its own paged-only body rules, off the
 * `--USER__view` value it writes inline on the root, so the reset follows a
 * layout change at runtime instead of being decided once at load.
 */
export const PAGED_FLOW_BODY_SELECTOR = `:root:not([style*="readium-scroll-on"]):not([${COVER_FRAME_MARKER}]) body`;

/**
 * Lets a paginated chapter fragment past its first column.
 *
 * Paginating makes the root a horizontal multi-column container one screen
 * tall and clips overflow on both root and body; ReadiumCSS sizes the body's
 * width there but never its height. So a publisher `height: 100%` on the body
 * — the opening line of many house stylesheets, and harmless everywhere that
 * paginates natively — goes unopposed and pins the body to a single column,
 * clipping the rest of the chapter away: one readable page, the last paragraph
 * cut mid-sentence. `max-height` pins it just the same.
 *
 * This is the paged counterpart to the root sizing ReadiumCSS already resets
 * for scroll mode, and mirrors it: a definite block-size becomes a minimum.
 * `min-height` keeps the box a full page tall, so a dedication or part title
 * that centres itself in a viewport-tall body — with flex, grid or `margin:
 * auto` — still has the page to centre in, while prose is free to run past it
 * and fragment. Removing the height outright collapsed those pages to their
 * text.
 */
export const PAGED_FLOW_CSS = `
${PAGED_FLOW_BODY_SELECTOR} {
    height: auto !important;
    max-height: none !important;
    min-height: 100% !important;
}`;

/**
 * The paged-flow reset for a publication of `layout`, or null when it needs
 * none. Fixed-layout frames get no column container, so there is no flow to
 * unblock and their `height: 100%` is load-bearing.
 *
 * @param {string} layout - Readium layout: 'reflowable' or 'fixed'
 * @returns {string|null}
 */
export const getPagedFlowCss = layout => (layout === 'fixed' ? null : PAGED_FLOW_CSS);

/**
 * The root of a cover frame. Repeating the marker outranks ReadiumCSS's
 * strongest rule on these properties, `:root[style*="--USER__lineLength"] body`.
 * A cascade layer would need no count, but `@layer` is past the iOS floor.
 */
export const COVER_FRAME_ROOT_SELECTOR = `:root[${COVER_FRAME_MARKER}][${COVER_FRAME_MARKER}]`;

/**
 * Percentages size a cover from the frame, so the body's zoom cannot grow it
 * past the frame. In scroll mode, the padding ReadiumCSS gives the body holds
 * it clear of the bars and the section button, also after a layout change.
 */
export const COVER_FRAME_CSS = `
${COVER_FRAME_ROOT_SELECTOR},
${COVER_FRAME_ROOT_SELECTOR} body {
    margin: 0 !important;
    width: 100% !important;
    height: 100% !important;
    min-height: 0 !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
}
${COVER_FRAME_ROOT_SELECTOR} body {
    max-width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
}
/* Columns keep the book's order and center every block, and the blocks share the height.
   A positioned wrapper leaves the column, so max-height is what limits it. */
${COVER_FRAME_ROOT_SELECTOR} [${COVER_FRAME_WRAPPER_MARKER}] {
    flex: 1 1 auto !important;
    width: 100% !important;
    max-height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
}
${COVER_FRAME_ROOT_SELECTOR} img {
    flex: 0 1 auto !important;
    max-height: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
    margin: 0 auto !important;
}
/* SVG-wrapped cover images (common in some EPUB generators) */
${COVER_FRAME_ROOT_SELECTOR} svg {
    flex: 1 1 auto !important;
    height: 100% !important;
    width: auto !important;
    max-width: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    margin: 0 auto !important;
}
`;

/**
 * Injected into every chapter document by the footnote interaction layer:
 * expands noteref hit areas to at least 24x24 CSS px without altering the
 * superscript's visual size (WCAG 2.5.8), and defines the fading highlight
 * applied to a note the reader jumps to. Chapter documents can parse as
 * XHTML (namespaced epub:type, matched by epub|type) or as HTML (literal
 * attribute name, matched by the escaped selector), so both selector forms
 * are emitted as separate rules.
 */
export const FOOTNOTE_CONTENT_STYLES = `
    @namespace epub "http://www.idpf.org/2007/ops";
    a[epub|type~="noteref"], a[role~="doc-noteref"] { position: relative; }
    a[epub\\:type~="noteref"] { position: relative; }
    a[epub|type~="noteref"]::after, a[role~="doc-noteref"]::after {
        content: ""; position: absolute; left: 50%; top: 50%;
        width: 100%; height: 100%; min-width: 24px; min-height: 24px;
        transform: translate(-50%, -50%);
    }
    a[epub\\:type~="noteref"]::after {
        content: ""; position: absolute; left: 50%; top: 50%;
        width: 100%; height: 100%; min-width: 24px; min-height: 24px;
        transform: translate(-50%, -50%);
    }
    @keyframes volpe-footnote-target-flash {
        0%, 40% { background-color: rgba(255, 196, 0, 0.35); box-shadow: 0 0 0 4px rgba(255, 196, 0, 0.35); }
        100% { background-color: transparent; box-shadow: 0 0 0 4px transparent; }
    }
    .volpe-footnote-target {
        animation: volpe-footnote-target-flash 2s ease-out forwards;
        border-radius: 2px;
    }
`;

/**
 * Dev-only overlay (pagebreaks=1 knob) that makes print-edition pagebreak
 * markers visible: a dashed rule at the break position plus a badge with the
 * printed page number.
 *
 * Only the rule is CSS. The badge is a real element that pagebreakDebugOverlay
 * injects into each marker, because it has to flip sides near the column edge
 * and CSS cannot express that: an out-of-flow box resolves to its static
 * position with no way to know it is about to overflow (see that module for why
 * anchor positioning doesn't cover it either). The overlay labels the markers
 * PagebreakTracker collects, so what renders is exactly what the tracker sees,
 * label included (aria-label > title, "p. ?" when the marker carries none).
 * Colors are hardcoded: theme tokens don't exist inside the content iframe, and
 * magenta stays visible on all reader themes.
 */
export const PAGEBREAK_DEBUG_CSS = `
/* Inline, so the break is marked without the false line wrap a block-level
   marker forced when it falls mid-paragraph. position: relative makes the marker
   the badge's containing block without moving it. */
[role="doc-pagebreak"] {
    position: relative !important;
    visibility: visible !important;
    height: auto !important;
    overflow: visible !important;
    border-left: 2px dashed #e0219e !important;
    line-height: 1 !important;
    pointer-events: none !important;
    z-index: 1 !important;
}

/* Out of flow, hanging under the rule: the badge overlays the text instead of
   taking inline space and pushing the words apart — which also keeps it out of
   the marker's own rect, so the tracker reads the same geometry with the overlay
   on or off. Faded by default so it never competes with the text underneath;
   hover to read it.

   Decoration only, never content. The marker is pointer-events: none and the
   badge opts back in, because :hover needs hit-testing — but that is the whole
   extent of its interactivity: user-select keeps its text out of the reader's
   selection (and out of anything built on that selection), and the default
   cursor stops it advertising itself as text. */
.volpe-pagebreak-label {
    position: absolute;
    top: 100%;
    left: 0;
    padding: 1px 0.4em 2px;
    border-radius: 0 0.5em 0.5em 0.5em;
    background: #e0219e;
    color: #fff;
    font: 700 11px/1.3 monospace;
    white-space: nowrap;
    opacity: 0.2;
    transition: opacity 0.12s ease-out;
    pointer-events: auto;
    cursor: default;
    -webkit-user-select: none;
    user-select: none;
}
.volpe-pagebreak-label:hover {
    opacity: 1;
}

/* Set by pagebreakDebugOverlay when hanging right would cross the column edge.
   right: 0 pins the badge's trailing edge to the (zero-width) marker, so it
   extends leftwards instead. */
.volpe-pagebreak-label--flip {
    left: auto;
    right: 0;
    border-radius: 0.5em 0 0.5em 0.5em;
}
`;
